import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface EmergencyProfile {
  conditions?: string;
  allergies?: string;
  bloodType?: string;
  nhsNumber?: string;
  gpName?: string;
  gpPhone?: string;
  nextOfKinName?: string;
  nextOfKinPhone?: string;
  hospitalPreference?: string;
}

interface Medication {
  id: string;
  name: string;
  dose?: string;
  timesPerDay: number;
  daysOfWeek: string[];
}

interface CardData {
  dependentName: string;
  dateOfBirth?: string;
  emergencyProfile?: EmergencyProfile;
  medications: Medication[];
  generatedAt: string;
}

function formatDob(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatFrequency(med: Medication): string {
  const times = med.timesPerDay === 1 ? 'once daily' : med.timesPerDay === 2 ? 'twice daily' : `${med.timesPerDay}× daily`;
  const days = med.daysOfWeek.length === 7 ? 'every day' : med.daysOfWeek.join(', ');
  return `${times} · ${days}`;
}

export function EmergencyCardPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CardData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/card/${token}`);
        if (res.status === 404) { setError('This emergency card link is no longer valid.'); return; }
        if (!res.ok) { setError('Unable to load emergency information.'); return; }
        setData(await res.json());
      } catch {
        setError('Unable to connect. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500 text-sm">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="text-4xl mb-4">🔒</div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Link not found</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const ep = data.emergencyProfile;

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Print button - hidden when printing */}
      <div className="print:hidden bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">Emergency Information · Kin</span>
        <button
          onClick={() => window.print()}
          className="text-xs px-3 py-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
        >
          Print
        </button>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="border-l-4 border-red-500 pl-4">
          <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-1">Emergency Information</p>
          <h1 className="text-3xl font-bold text-gray-900">{data.dependentName}</h1>
          {data.dateOfBirth && (
            <p className="text-sm text-gray-500 mt-0.5">DOB: {formatDob(data.dateOfBirth)}</p>
          )}
        </div>

        {/* Critical info */}
        {(ep?.bloodType || ep?.nhsNumber || ep?.conditions || ep?.allergies) && (
          <div className="space-y-2">
            {(ep?.bloodType || ep?.nhsNumber) && (
              <div className="flex gap-2">
                {ep?.bloodType && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm font-semibold">
                    🩸 {ep.bloodType}
                  </span>
                )}
                {ep?.nhsNumber && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium">
                    NHS: {ep.nhsNumber}
                  </span>
                )}
              </div>
            )}
            {ep?.conditions && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">Conditions</p>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{ep.conditions}</p>
              </div>
            )}
            {ep?.allergies && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">⚠ Allergies</p>
                <p className="text-sm text-red-900 font-medium whitespace-pre-wrap">{ep.allergies}</p>
              </div>
            )}
          </div>
        )}

        {/* Medications */}
        {data.medications.length > 0 && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Current Medications</h2>
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 bg-white">
              {data.medications.map((med) => (
                <div key={med.id} className="px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {med.name}{med.dose ? ` · ${med.dose}` : ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatFrequency(med)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GP */}
        {(ep?.gpName || ep?.gpPhone) && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">GP / Doctor</h2>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
              <div>
                {ep.gpName && <p className="text-sm font-medium text-gray-900">{ep.gpName}</p>}
                {ep.gpPhone && <p className="text-sm text-gray-500 mt-0.5">{ep.gpPhone}</p>}
              </div>
              {ep.gpPhone && (
                <a href={`tel:${ep.gpPhone}`} className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors">
                  Call
                </a>
              )}
            </div>
          </div>
        )}

        {/* Next of kin */}
        {(ep?.nextOfKinName || ep?.nextOfKinPhone) && (
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Next of Kin</h2>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
              <div>
                {ep.nextOfKinName && <p className="text-sm font-medium text-gray-900">{ep.nextOfKinName}</p>}
                {ep.nextOfKinPhone && <p className="text-sm text-gray-500 mt-0.5">{ep.nextOfKinPhone}</p>}
              </div>
              {ep.nextOfKinPhone && (
                <a href={`tel:${ep.nextOfKinPhone}`} className="text-sm px-3 py-1.5 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 transition-colors">
                  Call
                </a>
              )}
            </div>
          </div>
        )}

        {/* Hospital preference */}
        {ep?.hospitalPreference && (
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Hospital Preference</p>
            <p className="text-sm text-gray-800">{ep.hospitalPreference}</p>
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-300">
            Generated by Kin · {new Date(data.generatedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
